import { Hill } from '@/components/hill';
import { HillLegend } from '@/components/hill-legend';
import { HillTitle } from '@/components/hill-title';
import { MarkAsDoneButton } from '@/components/mark-as-done-button';
import { OrphanBanner } from '@/components/orphan-banner';
import { SnapshotPanel } from '@/components/snapshot-panel';
import { Button } from '@/components/ui/button';
import { hillsApi, snapshotsApi } from '@/lib/api';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { queryKeys } from '@/lib/query-keys';
import { snapshotToHillView } from '@/lib/snapshot-to-hill-view';
import { useHillSocket } from '@/lib/use-hill-socket';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  ArrowLeftIcon,
  CheckCircle2Icon,
  ExternalLinkIcon,
  RadioIcon,
  RefreshCwIcon,
  RewindIcon,
} from 'lucide-react';
import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';

/**
 * Display mode for the detail view.
 *
 *  - `'live'`: rendered at `/hills/:id`. Back link returns to the home
 *    list, WS subscribes for realtime updates, header shows the WS status
 *    badge, mutation buttons (Sync, Mark as Done, Take snapshot) appear
 *    when the hill is Active.
 *  - `'archive'`: rendered at `/archive/:id`. Back link returns to the
 *    archive list, WS is skipped (the server would refuse with 4423
 *    anyway), header surfaces the completion date instead of WS status,
 *    every mutation surface is hidden. Snapshot timeline stays browsable.
 *
 * Both modes share the rest of the page (header layout, two-column grid,
 * snapshot panel, view-mode banner). Driving mode-specific bits off a
 * single prop keeps the routes thin and avoids two parallel pages.
 */
export type HillDetailMode = 'live' | 'archive';

interface HillDetailViewProps {
  hillId: string;
  mode: HillDetailMode;
}

/**
 * Single-hill detail view, used by both `/hills/:id` (live) and
 * `/archive/:id` (read-only). The lifecycle of the hill itself
 * (`hill.completedAt !== null`) decides what's read-only at the data
 * layer (skip WS, hide buttons); the `mode` prop only controls the
 * navigation surface (back link, breadcrumb-like context).
 */
export function HillDetailView({ hillId, mode }: HillDetailViewProps) {
  /**
   * Which snapshot is currently being viewed, or `null` for the live hill.
   *
   * Two siblings need to agree on it: the panel highlights the selected
   * item, and the main view swaps between the live hill and
   * `snapshotToHillView(...)`. Lifting it up is the simplest way to
   * guarantee they can't disagree.
   */
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null);

  /**
   * The snapshot history panel (right column) is anchored to the height of
   * the hill column (left). Without this, a long snapshot list stretches
   * the grid row and drags the hill card down with it.
   *
   * Implementation:
   * - A callback ref captures the left column's DOM element when it mounts.
   *   We can't use a plain `useRef` + `useLayoutEffect([])` here because
   *   the component has early returns for the loading/error states: on
   *   the first layout commit the target div doesn't exist yet, so a
   *   one-shot effect would observe nothing. A callback ref fires whenever
   *   the element attaches or detaches, so the observer follows the
   *   render lifecycle correctly.
   * - `ResizeObserver` tracks the left column's content height and pushes
   *   it into state; the panel wrapper consumes it via a CSS variable.
   * - The outer grid switches to `md:items-start` so the right column no
   *   longer participates in the row's intrinsic-size negotiation.
   * - The constraint applies only at >=md (via a CSS var + `md:` utility)
   *   so on mobile the panel grows naturally.
   */
  const [leftColHeight, setLeftColHeight] = useState<number | null>(null);
  const [leftColEl, setLeftColEl] = useState<HTMLDivElement | null>(null);

  const leftColRefCallback = useCallback((node: HTMLDivElement | null) => {
    setLeftColEl(node);
  }, []);

  useEffect(() => {
    if (!leftColEl) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      // `contentRect.height` excludes border, which matches what CSS
      // `height: Xpx` will apply - no off-by-one between the two columns.
      setLeftColHeight(entry.contentRect.height);
    });
    observer.observe(leftColEl);
    return () => observer.disconnect();
  }, [leftColEl]);

  const qc = useQueryClient();

  const resyncMutation = useMutation({
    mutationFn: () => hillsApi.resync(hillId),
    onSuccess: (updatedHill) => {
      // Two-step write to guarantee the chart reflects the resync result
      // immediately, even in the face of a stale WS echo arriving after
      // the HTTP response (which would otherwise overwrite the new dot
      // payloads with the cached pre-resync ones).
      //
      // 1. `setQueryData` paints the new state synchronously — no flash
      //    of the old milestone names while the query refetches.
      // 2. `invalidateQueries` schedules a refetch so the cache becomes
      //    the authoritative source again. This is the belt-and-braces
      //    that closed the "rename a milestone in GitLab → click Sync →
      //    old title persists on the dot" report: even if any
      //    intermediate layer (WS handler, optimistic patch, …) skews
      //    the optimistic write, the refetch lands the truth.
      qc.setQueryData(queryKeys.hill(hillId), updatedHill);
      qc.invalidateQueries({ queryKey: queryKeys.hill(hillId) });
    },
  });

  // `staleTime: 0` here (vs 30s on the list) - the WS socket below owns
  // freshness while the tab is active, so a cache miss after an unmount
  // should refetch eagerly rather than serve potentially stale dots.
  const hillQuery = useQuery({
    queryKey: queryKeys.hill(hillId),
    queryFn: () => hillsApi.get(hillId),
    staleTime: 0,
  });

  /**
   * Subscribe to the same snapshot list the panel shows so we can look up
   * the selected snapshot by id without plumbing the full object through
   * `onSelect`. TanStack Query dedupes on the query key - this and the
   * panel's `useQuery` hit the cache once, not twice.
   */
  /**
   * Background twin of `resyncMutation`, fired once when the page opens.
   *
   * Why a second mutation rather than reusing the one above: the manual
   * Sync button renders its own pending state off `resyncMutation.isPending`
   * (spinning icon, "Syncing…", disabled). Auto-firing that mutation would
   * make the button look busy on every page load for a sync the user never
   * asked for. This one is deliberately silent - nothing in the UI is bound
   * to its state.
   *
   * `onError` is an explicit no-op, not an oversight. The manual Sync button
   * is where a sync failure is meant to be visible; a page load must never
   * turn into an error toast for a reconciliation the user did not request.
   * The hill itself loaded fine and is fully usable without it.
   *
   * No `invalidateQueries` here either (unlike the manual path): a silent
   * refetch racing the initial render would produce a visible flicker for no
   * user-facing gain. `setQueryData` with the server's own response is
   * authoritative enough for a background reconcile.
   *
   * It calls `resyncInBackground`, not `resync`: the background variant opts
   * out of the API client's GITLAB_TOKEN_EXPIRED redirect, so a dead or
   * missing refresh token cannot turn opening a page into a sign-in flow the
   * user never asked for. The manual Sync button keeps that redirect.
   */
  const autoResyncMutation = useMutation({
    mutationFn: () => hillsApi.resyncInBackground(hillId),
    onSuccess: (updatedHill) => {
      qc.setQueryData(queryKeys.hill(hillId), updatedHill);
    },
    onError: () => {
      // Intentionally swallowed - see the block comment above.
    },
  });

  /**
   * Guard: the resync must fire exactly once per mount. `hillQuery.data` is
   * a dependency (we cannot decide whether to sync before knowing the hill's
   * provider and lifecycle), and it changes again as soon as the resync
   * writes its result back into the cache - so without this ref the effect
   * would re-enter and hammer GitLab on every render.
   */
  const autoResyncedRef = useRef(false);

  /**
   * Silent reconcile when an active GitLab hill is opened.
   *
   * There is no webhook ingress since the GitLab migration, so a hill only
   * learns about upstream milestone changes when something asks. The moment
   * the user opens the page is the cheapest such moment: their OAuth token
   * is fresh and they are about to look at the dots anyway. Any change the
   * resync produces also reaches every other connected client through the
   * usual `dot:*` WS broadcasts.
   *
   * This is a fire-and-forget side effect, not data fetching for render:
   * TanStack Query still owns the hill itself (`hillQuery` above), and the
   * request goes through `useMutation`, not a hand-rolled `fetch`. The
   * effect is only the trigger - which is why the "no useEffect for data
   * fetching" rule in CLAUDE.md is respected in intent as well as in form.
   *
   * Skipped for:
   *  - legacy `provider: 'linear'` hills - their Linear project is gone and
   *    the backend answers 409 LEGACY_PROVIDER;
   *  - Done hills - the backend answers 409 HILL_COMPLETED;
   *  - the archive route - a read-only surface by construction.
   */
  useEffect(() => {
    if (autoResyncedRef.current) return;
    if (mode !== 'live') return;
    const loadedHill = hillQuery.data;
    if (!loadedHill) return;
    if (loadedHill.completedAt !== null) return;
    if (loadedHill.provider !== 'gitlab') return;

    autoResyncedRef.current = true;
    autoResyncMutation.mutate();
    // The dep is `autoResyncMutation.mutate`, not `autoResyncMutation`: only
    // `.mutate` is a stable reference across renders. The object itself is
    // rebuilt on every mutation state transition, so depending on it would
    // re-run the effect on each one - absorbed by the ref guard, but noise.
  }, [hillQuery.data, mode, autoResyncMutation.mutate]);

  const snapshotsQuery = useQuery({
    queryKey: queryKeys.snapshots(hillId),
    queryFn: () => snapshotsApi.list(hillId),
  });

  const isDone = hillQuery.data?.completedAt != null;

  // Subscribe to WS only for ACTIVE hills. A Done hill's room is closed
  // server-side and any upgrade attempt returns 4423 immediately - the
  // hook can handle that, but skipping it entirely avoids the brief
  // "Connecting -> Reconnecting" flicker while the close lands.
  const socket = useHillSocket(hillQuery.data && !isDone ? hillId : null);

  if (hillQuery.isLoading) {
    return (
      <section className="space-y-6">
        <div className="h-8 w-64 animate-pulse rounded bg-slate-100" />
        <div className="h-[400px] animate-pulse rounded-lg border border-slate-200 bg-slate-50" />
      </section>
    );
  }

  const backTo = mode === 'archive' ? '/archive' : '/hills';
  const backLabel = mode === 'archive' ? 'Back to archive' : 'Back to hills';

  if (hillQuery.isError) {
    return (
      <section className="space-y-4">
        <Link
          to={backTo}
          className="inline-flex items-center gap-2 text-slate-600 text-sm hover:text-slate-900"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {backLabel}
        </Link>
        <div
          role="alert"
          className="flex flex-col gap-4 rounded-lg border border-red-200 bg-red-50 p-6 text-red-700 sm:flex-row sm:items-start sm:justify-between"
        >
          <div>
            <p className="font-medium">Couldn't load this hill.</p>
            <p className="mt-1 text-sm">{hillQuery.error.message}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => hillQuery.refetch()}
            disabled={hillQuery.isFetching}
            className="shrink-0 border-red-300 text-red-700 hover:bg-red-100"
          >
            {hillQuery.isFetching ? 'Retrying…' : 'Try again'}
          </Button>
        </div>
      </section>
    );
  }

  const hill = hillQuery.data;
  if (!hill) return null;

  /**
   * Resolve the snapshot we're viewing (if any) against the cached list.
   * Returns `undefined` when no snapshot is selected (live view) or when
   * the selected id is no longer in the list (silent fallback to live).
   */
  const selectedSnapshot =
    selectedSnapshotId === null
      ? undefined
      : snapshotsQuery.data?.snapshots.find((s) => s.id === selectedSnapshotId);

  // The view model fed into `<Hill />`: either the live hill or a
  // reconstructed hill view built from the snapshot. Branching here
  // (rather than inside the renderer) keeps `<Hill />` oblivious to the
  // snapshot concept.
  const viewedHill = selectedSnapshot ? snapshotToHillView(selectedSnapshot, hill) : hill;
  const isViewingSnapshot = selectedSnapshot !== undefined;
  // Read-only locks dot drag + hides mutation surfaces. Triggered by
  // either browsing a past snapshot OR the hill being Done.
  const readOnly = isViewingSnapshot || isDone;

  return (
    <section className="space-y-6">
      <Link
        to={backTo}
        className="inline-flex items-center gap-2 text-slate-600 text-sm hover:text-slate-900"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        {backLabel}
      </Link>

      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          {/*
            The title is user-owned and inline-editable. `readOnly` already
            expresses both cases where renaming must be off (Done hill, or a
            past snapshot being browsed), so it is reused verbatim rather
            than recomputed here.
          */}
          <HillTitle hill={hill} readOnly={readOnly} />
          <div className="flex items-center gap-3 text-slate-500 text-sm">
            {/*
              Which upstream project backs this hill. The title is user-owned
              and free to diverge from `externalProjectName`, so without this
              line there is no way to tell from the page which project the
              Sync button actually talks to. The provider prefix names the
              system so the two eras of hills read differently at a glance.
            */}
            <span data-testid="backing-project" className="max-w-64 truncate">
              {hill.provider === 'linear' ? 'Linear' : 'GitLab'}: {hill.externalProjectName}
            </span>
            <span>·</span>
            {/*
              Live hills show the WS status badge so the user knows whether
              they're seeing realtime updates. Done hills replace it with
              a small "Done" indicator carrying the completion date.
            */}
            {isDone && hill.completedAt !== null ? (
              <span className="inline-flex items-center gap-1.5" data-testid="done-status">
                <CheckCircle2Icon className="h-4 w-4 text-amber-600" />
                <span>Done {formatRelativeTime(hill.completedAt)}</span>
              </span>
            ) : (
              <span aria-live="polite" data-testid="ws-status">
                {socket.status === 'open' && (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
                    Live
                  </span>
                )}
                {socket.status === 'connecting' && (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-amber-400" />
                    Connecting…
                  </span>
                )}
                {socket.status === 'closed' && (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-2 w-2 rounded-full bg-slate-400" />
                    Reconnecting…
                  </span>
                )}
                {socket.status === 'idle' && (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-2 w-2 rounded-full bg-slate-300" />
                    Offline
                  </span>
                )}
              </span>
            )}
            <span>·</span>
            <span>
              {hill.dots.length} {hill.dots.length === 1 ? 'scope' : 'scopes'}
            </span>
            <span>·</span>
            <span data-testid="created-at">Created {formatRelativeTime(hill.createdAt)}</span>
            {/*
              Legacy badge: this hill was imported from Linear before the
              GitLab migration. Its upstream Linear project no longer backs
              any sync path, so the Sync button is hidden for it (see
              below) - the badge tells the user why at a glance, in the
              same visual family as the orphan banner and the Done badge.
            */}
            {hill.provider === 'linear' && (
              <>
                <span>·</span>
                <span
                  data-testid="legacy-badge"
                  className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 font-medium text-slate-600 text-xs"
                >
                  Legacy (Linear)
                </span>
              </>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {/*
            Sync and Mark as Done are hidden while the user is browsing a
            past snapshot - those actions only make sense against the live
            state, not against a frozen replay. They are also hidden once
            the hill is Done because the backend rejects them with 409
            HILL_COMPLETED; surfacing the buttons would just produce error
            toasts when clicked.

            Sync is additionally restricted to `provider === 'gitlab'`: a
            legacy hill's Linear project is gone, so the backend always
            rejects its resync with 409 LEGACY_PROVIDER - offering the
            button would just produce an error toast on every click.
          */}
          {!isViewingSnapshot && !isDone && hill.provider === 'gitlab' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => resyncMutation.mutate()}
              disabled={resyncMutation.isPending}
              title="Sync milestones from GitLab"
            >
              <RefreshCwIcon
                className={`h-4 w-4 ${resyncMutation.isPending ? 'animate-spin' : ''}`}
              />
              {resyncMutation.isPending ? 'Syncing…' : 'Sync'}
            </Button>
          )}
          {!isViewingSnapshot && !isDone && <MarkAsDoneButton hill={hill} />}

          {/*
            The link points at `externalProjectUrl`, which is a GitLab URL on
            every hill imported since the migration and a Linear one only on
            legacy rows. The label follows the provider so it never announces
            the wrong destination.
          */}
          {hill.externalProjectUrl && (
            <Button asChild variant="outline" size="sm">
              <a
                href={hill.externalProjectUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-2"
              >
                Open in {hill.provider === 'linear' ? 'Linear' : 'GitLab'}
                <ExternalLinkIcon className="h-4 w-4" />
              </a>
            </Button>
          )}
        </div>
      </header>

      {/*
        Orphan warning: shows only when the upstream project has been deleted
        AND the hill is still Active. Sits above the two-column
        layout so it sticks to the top of the visible content. Self-hides
        when both conditions don't hold - safe to render unconditionally.
      */}
      <OrphanBanner
        externalProjectDeletedAt={hill.externalProjectDeletedAt}
        completedAt={hill.completedAt}
      />

      {/*
        Two-column layout: the hill view on the left, snapshot history on
        the right. `grid-cols-[1fr_320px]` fixes the drawer width so the
        chart reflows as the window resizes while the list width stays
        readable. Below md we stack them.
      */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_320px] md:items-start">
        <div ref={leftColRefCallback} className="space-y-4">
          {/*
            View-mode banner anchors the chart at a stable Y offset across
            live/snapshot transitions. Two visual variants but identical
            shell so toggling snapshot view doesn't shove the chart down.
          */}
          <ViewModeBanner
            capturedAt={selectedSnapshot?.capturedAt ?? null}
            note={selectedSnapshot?.note ?? null}
            isDone={isDone}
            onBackToLive={() => setSelectedSnapshotId(null)}
          />

          <div className="rounded-lg border border-slate-200 bg-white p-6">
            {viewedHill.dots.length === 0 ? (
              <p className="py-12 text-center text-slate-500 text-sm">
                {/*
                  Three cases, not two. A freshly imported GitLab hill with no
                  milestones is the common one and the copy has to name the
                  system the user should go and act in. A legacy hill gets
                  different wording: its Sync button is hidden and the backend
                  refuses its resync with 409 LEGACY_PROVIDER, so promising it
                  scopes "on the next sync" would be a lie.
                */}
                {isViewingSnapshot
                  ? 'This snapshot captured zero scopes.'
                  : hill.provider === 'gitlab'
                    ? "This project has no milestones yet. Add one in GitLab and it'll appear here on the next sync."
                    : 'This legacy hill has no scopes, and it can no longer sync with Linear.'}
              </p>
            ) : (
              <>
                <Hill hill={viewedHill} viewId={selectedSnapshotId ?? 'live'} readonly={readOnly} />
                <HillLegend dots={viewedHill.dots} />
              </>
            )}
          </div>
        </div>

        <div
          className="md:h-[var(--left-col-h,auto)]"
          style={
            leftColHeight !== null
              ? ({ '--left-col-h': `${leftColHeight}px` } as CSSProperties)
              : undefined
          }
        >
          <SnapshotPanel
            hillId={hillId}
            selectedId={selectedSnapshotId}
            onSelect={setSelectedSnapshotId}
            readOnly={isDone}
          />
        </div>
      </div>
    </section>
  );
}

interface ViewModeBannerProps {
  /**
   * ISO-8601 capture time of the selected snapshot, or `null` when no
   * snapshot is selected (the user is viewing the canonical hill).
   * Presence of this value switches the banner into its amber snapshot
   * variant - we derive the mode from data rather than a separate prop
   * so the two can't disagree.
   */
  capturedAt: string | null;
  /** Optional user-provided note on the snapshot, if any. */
  note: string | null;
  /**
   * `true` when the hill is archived (`completedAt` is non-null). Drives
   * the "no snapshot selected" variant: a Done hill has no live mode, so
   * the green "Live view" banner becomes a slate "Final state" banner
   * pointing out the read-only nature.
   */
  isDone: boolean;
  /**
   * Returns the view to the canonical hill. Called from snapshot mode.
   * On a live hill that means "Back to live"; on an archived hill it
   * means "Back to final state" - the action is the same (clear the
   * selected snapshot id), only the label changes.
   */
  onBackToLive: () => void;
}

/**
 * Status banner above the hill. Three variants, each visually consistent
 * (same shell, same min-height) so toggling between them never shoves the
 * chart down by a row of pixels:
 *
 *  - Live (live hill, no snapshot selected): emerald, "Live view".
 *  - Final state (archived hill, no snapshot selected): slate, read-only
 *    notice. We do not show a green "Live" banner on a Done hill - there
 *    is no live mode, and the previous behaviour was misleading.
 *  - Snapshot (any hill, snapshot selected): amber, "Viewing snapshot
 *    from X" with a "Back to live" / "Back to final state" button.
 */
function ViewModeBanner({ capturedAt, note, isDone, onBackToLive }: ViewModeBannerProps) {
  const isSnapshot = capturedAt !== null;
  // Pick a colour scheme upfront: snapshot wins, then archived, then live.
  const tone = isSnapshot
    ? 'border-amber-200 bg-amber-50 text-amber-900'
    : isDone
      ? 'border-slate-200 bg-slate-50 text-slate-700'
      : 'border-emerald-200 bg-emerald-50/60 text-emerald-900';
  return (
    <output
      className={`flex min-h-14 items-center justify-between gap-3 rounded-lg border px-4 py-3 ${tone}`}
    >
      <div className="flex items-start gap-2">
        {isSnapshot ? (
          <RewindIcon className="mt-0.5 h-4 w-4 shrink-0" />
        ) : (
          <RadioIcon className="mt-0.5 h-4 w-4 shrink-0" />
        )}
        <div className="text-sm">
          <span className="font-medium">
            {isSnapshot
              ? `Viewing snapshot from ${formatRelativeTime(capturedAt)}`
              : isDone
                ? 'Final state — this hill is archived and read-only'
                : 'Live view — changes stream in as teammates move dots'}
          </span>
          {isSnapshot && note !== null && note.length > 0 && (
            <p className="mt-0.5 text-amber-800 text-xs italic">{note}</p>
          )}
        </div>
      </div>
      {isSnapshot ? (
        <Button
          variant="outline"
          size="sm"
          onClick={onBackToLive}
          className="border-amber-300 text-amber-900 hover:bg-amber-100"
          data-testid="back-to-live"
        >
          {isDone ? 'Back to final state' : 'Back to live'}
        </Button>
      ) : !isDone ? (
        // Live hill: pulsing emerald dot mirrors the WS "Live" badge in
        // the page header, preserving horizontal balance with the
        // snapshot variant's "Back to live" button.
        <span
          aria-hidden="true"
          className="inline-flex items-center gap-2 pr-1 text-emerald-700 text-xs"
        >
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
        </span>
      ) : null}
    </output>
  );
}
